import { LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import * as React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  AppShell,
  BrandLogo,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Sidebar,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { useAuth } from '../auth/auth-context';
import { InmobiliariaActivaProvider, useInmobiliariaActiva } from '../features/admin/inmobiliaria-activa/inmobiliaria-activa';
import { visibleNavigation, type NavigationItem } from '../features/shell/navigation';

// Texto del menú que se oculta al colapsar: se desvanece y se desplaza a la izquierda sincronizado
// con el ancho del sidebar (tokens `sidebar`), y vuelve igual al expandir. Sigue en el DOM en ambos
// estados para que la transición exista; colapsado queda fuera del árbol de accesibilidad porque el
// enlace ya tiene `aria-label`.
function etiquetaMenu(collapsed: boolean): string {
  return cn(
    'transition-[opacity,transform] duration-sidebar ease-sidebar',
    'motion-reduce:translate-x-0 motion-reduce:transition-none',
    collapsed ? 'opacity-0 -translate-x-2' : 'opacity-100 translate-x-0',
  );
}

function NavLinks({
  items,
  collapsed,
}: {
  items: NavigationItem[];
  collapsed: boolean;
}): React.JSX.Element {
  return (
    <nav aria-label="Secciones" className="space-y-1">
      {items.map((item) => {
        const Icon = item.icon;
        // La estructura es la misma colapsado y expandido. El Tooltip se engancha a un contenedor y
        // no al NavLink: el Slot de Radix descarta el `className` función de NavLink y el enlace se
        // quedaba sin layout (ícono pegado a la izquierda). Ver change-62.
        return (
          <Tooltip key={item.to}>
            <TooltipTrigger asChild>
              <div>
                <NavLink
                  to={item.to}
                  // fix-50: `end` en los items cuya ruta es prefijo de otra (/separations vs /separations/new).
                  end={item.end}
                  aria-label={item.label}
                  className={({ isActive }) =>
                    cn(
                      'flex w-full items-center gap-3 overflow-hidden whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      // Mismo anillo de foco que Button; cabe dentro de la barra (px-3) aun colapsada.
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-accent',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span
                    data-sidebar-label=""
                    aria-hidden={collapsed ? true : undefined}
                    className={etiquetaMenu(collapsed)}
                  >
                    {item.label}
                  </span>
                </NavLink>
              </div>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
          </Tooltip>
        );
      })}
    </nav>
  );
}

/**
 * fix-60: al cambiar la inmobiliaria de trabajo se vuelve a montar la página (formularios, pasos del
 * catálogo y diálogos arrancan de cero; TC-46) y la caché queda separada por la clave de las consultas.
 */
function OutletPorInmobiliaria(): React.JSX.Element {
  const { inmobiliariaId } = useInmobiliariaActiva();
  return <Outlet key={inmobiliariaId ?? 'sesion'} />;
}

export function AppShellLayout(): React.JSX.Element {
  return (
    <InmobiliariaActivaProvider>
      <AppShellLayoutContenido />
    </InmobiliariaActivaProvider>
  );
}

function AppShellLayoutContenido(): React.JSX.Element {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = React.useState(false);
  const items = user ? visibleNavigation(user) : [];

  return (
    <TooltipProvider delayDuration={200}>
      <AppShell
        sidebarCollapsed={collapsed}
        sidebar={
          <Sidebar aria-label="Navegación principal" collapsed={collapsed}>
            {/* Encabezado con la misma geometría que un ítem del menú: el isotipo ocupa el ancho de un
                enlace colapsado (2.5rem), queda centrado en la barra colapsada y no se mueve; el
                wordmark y el subtítulo se ocultan con la misma transición que las etiquetas. Solo un
                logo es accesible a la vez para no anunciar «La agencia» dos veces. */}
            <div className="mb-6 flex items-center gap-3 overflow-hidden whitespace-nowrap border-b border-border pb-5">
              <span className="flex w-10 shrink-0 justify-center">
                <BrandLogo variant="isotipo" size="md" aria-hidden={collapsed ? undefined : true} />
              </span>
              <div aria-hidden={collapsed ? true : undefined} className={etiquetaMenu(collapsed)}>
                <BrandLogo variant="wordmark" size="sm" />
                <p className="mt-1.5 text-sm font-semibold text-foreground">Plan de pagos</p>
              </div>
            </div>
            <NavLinks items={items} collapsed={collapsed} />
          </Sidebar>
        }
        header={
          <div className="flex min-h-16 items-center justify-between gap-4 px-4 sm:px-6">
            <div className="flex items-center gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" aria-label="Abrir menú">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="left-0 top-0 h-full max-w-xs translate-x-0 translate-y-0 rounded-none p-6">
                  <DialogTitle>Navegación principal</DialogTitle>
                  <div className="mt-6">
                    <NavLinks items={items} collapsed={false} />
                  </div>
                </DialogContent>
              </Dialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden md:inline-flex"
                    aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                    aria-expanded={!collapsed}
                    onClick={() => setCollapsed((value) => !value)}
                  >
                    {collapsed ? (
                      <PanelLeftOpen className="h-5 w-5" />
                    ) : (
                      <PanelLeftClose className="h-5 w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{collapsed ? 'Expandir menú' : 'Colapsar menú'}</TooltipContent>
              </Tooltip>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-foreground">{user?.nombre}</p>
                <p className="text-xs text-muted-foreground">{user?.rol}</p>
              </div>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => void logout()}>
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Cerrar sesión</span>
              </Button>
            </div>
          </div>
        }
      >
        <OutletPorInmobiliaria />
      </AppShell>
    </TooltipProvider>
  );
}
